<template>
  <div style="position: relative; width: 100%; height: 100%;">
    <canvas ref="chart" />
    <slot />
  </div>
</template>

<script>
import Chart from "chart.js";
import { cloneDeep } from "lodash";
/**
 * Usage:
  <ChartJSComponent
    style="height: 100%"
    type="bar"
    :labels="labels"
    :datasets="datasets"
  />

  Horizontal Scroll Example:
  <div style="overflow-x: auto">
    <ChartJSComponent
      style="height: 100%; width: 1200px"
      type="bar"
      :labels="labels"
      :datasets="datasets"
    />
  </div>
 */
export default {
  props: {
    /**
     * More detail: http://www.chartjs.org/docs/latest/getting-started/usage.html
     * E.g.
      {
        datasets: [{
          label: '# of Votes',
          data: [12, 19, 3, 5, 2, 3],
          backgroundColor: [
            'rgba(255, 99, 132, 0.2)',
            'rgba(54, 162, 235, 0.2)',
            'rgba(255, 206, 86, 0.2)',
            'rgba(75, 192, 192, 0.2)',
            'rgba(153, 102, 255, 0.2)',
            'rgba(255, 159, 64, 0.2)'
          ],
          borderColor: [
            'rgba(255, 99, 132, 1)',
            'rgba(54, 162, 235, 1)',
            'rgba(255, 206, 86, 1)',
            'rgba(75, 192, 192, 1)',
            'rgba(153, 102, 255, 1)',
            'rgba(255, 159, 64, 1)'
          ],
          borderWidth: 1
        }]
      }
     *
     */
    datasets: {
      type: Array,
      required: true
    },

    /**
     * More detail: http://www.chartjs.org/docs/latest/getting-started/usage.html
     * E.g.
      {
        labels: ['Red', 'Blue', 'Yellow', 'Green', 'Purple', 'Orange'],
      }
     */
    labels: {
      type: Array,
      required: true
    },

    /**
     * More detail: https://www.chartjs.org/docs/latest/charts/
     *
     */
    type: {
      type: String,
      default: "line",
      validator: (value) => {
        return (
          [
            "bar",
            "line",
            "radar",
            "doughnut",
            "pie",
            "polarArea",
            "bubble",
            "scatter",
            "horizontalBar"
          ].indexOf(value) !== -1
        );
      }
    },

    /**
     * More detail: http://www.chartjs.org/docs/latest/getting-started/usage.html
     */
    options: {
      type: Object,
      default: function () {
        return {};
      }
    },

    /**
     * More detail: https://www.chartjs.org/docs/latest/developers/plugins.html
     */
    plugins: {
      type: Array,
      default: function () {
        return [];
      }
    }
  },

  data() {
    return {
      chart: null
    };
  },

  watch: {
    datasets(val) {
      if (val) this.updateDatasets(val);
    },

    labels(val) {
      if (val) this.updateLabels(val);
    },

    options(val) {
      if (val) this.updateOptions(val);
    },

    type(type) {
      if (type) this.updateType(type);
    },
    plugins(plugins) {
      if (plugins && plugins.length > 0) this.initChart();
    }
  },

  mounted() {
    this.initChart();
  },

  beforeDestroy() {
    this.destroyChart();
  },

  methods: {
    initChart() {
      this.destroyChart();

      const defaultOptions = {
        responsive: true,
        maintainAspectRatio: false,
      };

      if (this.type !== 'pie' && this.type !== 'doughnut' && this.type !== 'polarArea') {
        defaultOptions.scales = {
          yAxes: [
            {
              ticks: {
                beginAtZero: true
              }
            }
          ]
        };
      }

      this.chart = new Chart(this.$refs["chart"], {
        type: this.type,

        data: {
          labels: this.labels.slice(),
          datasets: this.datasets
        },
        plugins: this.plugins,
        options: Object.assign({}, defaultOptions, this.options)
      });
    },

    destroyChart() {
      if (this.chart) {
        this.chart.destroy();
        this.chart = null;
      }
    },

    updateDatasets(data) {
      // Must clone data, DO NOT DIRECTLY USE VUEJS/VUEX DATA
      // it will occurs 'Maximum call stack size exceeded' and it hard to debug
      // almost can not find bug by debugging, i have searched in stack overflow, i though it's bug of vuejs, vuex or element-ui but it's not
      // it because conflict of chartjs with vuejs, chartjs make vuejs data error
      this.chart.data.datasets = cloneDeep(data);
      this.chart.data.labels = cloneDeep(this.labels);
      this.chart.update();
    },

    updateLabels(labels) {
      this.chart.data.datasets = cloneDeep(this.datasets);
      this.chart.data.labels = cloneDeep(labels);
      this.chart.update();
    },

    updateOptions(data) {
      this.chart.options = cloneDeep(data);
      this.chart.update();
    },

    updateType(type) {
      this.initChart();
    }
  }
};
</script>
